import React, { useEffect, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { CommonActions } from "@react-navigation/native";
import tw from "twrnc";

import analytics from "@/analytics";
import { useAuth } from "@/components/AuthContext";
import Container from "@/components/shared/AppContainer";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import AppHeader from "@/components/shared/AppHeader";
import { notify } from "@/components/shared/AppToast";
import Button, { ButtonStack } from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { CONTENT_MAX_WIDTH, text } from "@/components/settings/theme";
import { t } from "@/localization";
import { refreshSharedAccess } from "@/components/teams/actions";
import { acceptTeamInvite, fetchTeamInvite } from "@/components/teams/api";
import { teamsErrorMessage } from "@/components/teams/errors";
import { openWorkspace } from "@/components/teams/openWorkspace";
import {
  clearPendingInvite,
  isInviteToken,
  storePendingInvite,
} from "@/components/teams/pendingInvite";
import RolePill from "@/components/teams/RolePill";
import {
  accountNameOf,
  ROLE_INVITEE_DESCRIPTIONS,
} from "@/components/teams/roles";

// invite/<token>: the page a team invite email links to (PLAN.md §5.4). It
// shows who invited you, to what and as what, before anyone signs in (the
// public teamInvite preview). Signed out: sign in or sign up, then you come
// back here. Signed in: "Accept as you@example.com", needed for Apple and
// Google sign-ins whose email differs from the invited one. Accepting needs
// the link, never just a matching email.

const STATUS_MESSAGES = {
  invalid: t("This invite link isn't valid. Ask for a new one."),
  expired: t("This invite has expired. Ask for a new one."),
  accepted: t("This invite was already used."),
  revoked: t(
    "This invite was withdrawn. Ask for a new one if you still need access."
  ),
};

const TeamInvite = ({ route, navigation }) => {
  const token = route?.params?.token;
  const { isSignedOut, isLoading, email, signOut } = useAuth();
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(
    isInviteToken(token) ? "loading" : "invalid"
  );
  const [loadError, setLoadError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);

  const load = async () => {
    if (!isInviteToken(token)) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    setLoadError(null);
    try {
      const result = await fetchTeamInvite(token);
      setPreview(result);
      setStatus(result ? result.status : "invalid");
    } catch (err) {
      setLoadError(
        teamsErrorMessage(err, t("Couldn't open this invite. Try again."))
      );
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  // Back here after signing in: the invite no longer needs keeping.
  useEffect(() => {
    if (!isSignedOut && !isLoading) clearPendingInvite();
  }, [isSignedOut, isLoading]);

  const goHome = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: isSignedOut ? "SignUpSignIn" : "EventList" }],
      })
    );
  };

  const signIn = async () => {
    await storePendingInvite(token);
    navigation.navigate("SignUpSignIn");
  };

  const useAnotherAccount = async () => {
    await storePendingInvite(token);
    await signOut();
  };

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const access = await acceptTeamInvite(token);
      await clearPendingInvite();
      if (!access?.owner)
        throw new Error(t("This invite link isn't valid. Ask for a new one."));
      const scope = access.slug ? "event" : "account";
      analytics.track("Team Invite Accepted", { role: access.role, scope });
      await refreshSharedAccess({ force: true });
      notify.success(
        access.slug
          ? access.event_title
            ? t("You can now work on {title}", { title: access.event_title })
            : t("You can now work on the event")
          : t("You joined {team}'s account", { team: accountNameOf(access) })
      );
      const opened = await openWorkspace(navigation, access.owner, {
        slug: access.slug || null,
        source: "invite",
      });
      if (!opened.ok && !opened.stale) goHome();
    } catch (err) {
      setAcceptError(
        teamsErrorMessage(err, t("Couldn't accept the invite. Try again."))
      );
    } finally {
      setAccepting(false);
    }
  };

  const scope = preview?.event_title ? "event" : "account";
  const target =
    preview?.event_title || preview?.account_name || t("their account");
  const inviter = preview?.inviter_name || t("Someone");
  const forYouthStream = preview?.app === "youthstream";
  const waitingForAuth = isLoading && !isSignedOut;

  return (
    <Container>
      <AppHeader hasLeftButton={false} hasSupportButton={false} />
      <ScrollView
        contentContainerStyle={[
          tw`w-full self-center px-5 pt-2 pb-12`,
          { maxWidth: CONTENT_MAX_WIDTH + 40 },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <SettingsPage
          title={t("You're invited")}
          description={
            status === "pending"
              ? t(
                  "Join with your own sign-in. Your own account and events stay yours."
                )
              : undefined
          }
        >
          {(status === "loading" || waitingForAuth) && <AppActivityIndicator />}

          {status === "error" && (
            <Callout
              tone="danger"
              action={
                <Button
                  text={t("Try again")}
                  size="sm"
                  fullWidth={false}
                  onPress={load}
                />
              }
            >
              {loadError}
            </Callout>
          )}

          {!!STATUS_MESSAGES[status] && (
            <Callout
              tone={status === "accepted" ? "info" : "warning"}
              action={
                <Button
                  text={
                    isSignedOut
                      ? t("Sign in to EventLive")
                      : t("Go to my events")
                  }
                  size="sm"
                  fullWidth={false}
                  onPress={goHome}
                />
              }
            >
              {STATUS_MESSAGES[status]}
            </Callout>
          )}

          {status === "pending" && !waitingForAuth && (
            <View>
              <Callout tone="neutral">
                <Text style={text.bodyStrong}>
                  {t("{inviter} invited you to help with {target}.", {
                    inviter,
                    target,
                  })}
                </Text>
                <View style={tw`flex-row items-center mt-3`}>
                  <RolePill role={preview.role} />
                  {!!preview.event_title && !!preview.account_name && (
                    <Text
                      style={[text.hint, tw`ml-2 flex-1`]}
                      numberOfLines={1}
                    >
                      {preview.account_name}
                    </Text>
                  )}
                </View>
                <Text style={[text.body, tw`mt-2`]}>
                  {ROLE_INVITEE_DESCRIPTIONS[scope][preview.role]}
                </Text>
                {!!preview.email_hint && (
                  <Text style={[text.hint, tw`mt-2`]}>
                    {t("Sent to {email}", { email: preview.email_hint })}
                  </Text>
                )}
              </Callout>

              {forYouthStream ? (
                <Callout tone="info" style={tw`mt-5`}>
                  {t(
                    "This invite is for the YouthStream app. Open the link on your iPhone, or sign in to YouthStream with the email it was sent to."
                  )}
                </Callout>
              ) : isSignedOut ? (
                <View style={tw`mt-6`}>
                  <Button
                    text={t("Sign in to accept")}
                    variant="primary"
                    onPress={signIn}
                  />
                  <Text style={[text.hint, tw`mt-3 text-center`]}>
                    {t(
                      "New to EventLive? Continue with your email to create a free account, then accept."
                    )}
                  </Text>
                </View>
              ) : (
                <ButtonStack style={tw`mt-6`}>
                  <Button
                    text={
                      email
                        ? t("Accept as {email}", { email })
                        : t("Accept invite")
                    }
                    variant="primary"
                    loading={accepting}
                    onPress={accept}
                  />
                  <Button
                    text={t("Use another account")}
                    variant="tertiary"
                    fullWidth={false}
                    style={tw`self-center`}
                    disabled={accepting}
                    onPress={useAnotherAccount}
                  />
                </ButtonStack>
              )}

              {!!acceptError && (
                <Callout tone="danger" style={tw`mt-4`} live>
                  {acceptError}
                </Callout>
              )}
            </View>
          )}
        </SettingsPage>
      </ScrollView>
    </Container>
  );
};

export default TeamInvite;
